import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import compact from 'lodash/compact';
import get from 'lodash/get';
import http from '../../../shared/http';
import endpoints from '../../../../config/endpoints';
import config from './config';
import { actions as parentActions, selectors as parentSelectors } from '../../../admins';

const createPermissions = ({
	namespaces,
	stateNodeName
}) => {

	const TOGGLE_SELECT = `${namespaces}/${stateNodeName}--toggle-select`;
	const TOGGLE_SELECT_VALUE = `${namespaces}/${stateNodeName}--toggle-select-value`;
	const SET_INITIAL_VALUES = `${namespaces}/${stateNodeName}--set-initial-values`;
	const SAVE_PERMISSION_SUCCESS = `${namespaces}/${stateNodeName}--save-success`;
	const SAVE_PERMISSION_ERROR = `${namespaces}/${stateNodeName}--save-error`;
	const TOGGLE_SUBPERMISSION = `${namespaces}/${stateNodeName}--toggle-subpermission`;

	// initial state
	const initialState = {
		items: config.map(item => ({
			placeholder: item.moduleName,
			name: item.moduleKey,
			isOpen: false,
			isVisible: !item.isHidden,
			subPermission: item.subPermission,
			options: item.rules.map(rule => ({
				label: rule.name,
				value: rule.key,
				icon: rule.icon,
				getIsHidden: rule.getIsHidden,
				isSelected: false
			}))
		}))
	};

	// actions
	const actions = {
		permissionsControlToggle: controlName => ({
			type: TOGGLE_SELECT,
			payload: controlName
		}),

		permissionControlToggleSelectValue: (controlName, optionValue) => (dispatch, getState) => {
			dispatch({
				type: TOGGLE_SELECT_VALUE,
				payload: {
					controlName,
					optionValue
				}
			});

			dispatch({
				type: TOGGLE_SUBPERMISSION,
				payload: {
					controlName,
					optionValue
				}
			});

			const items = getState()[namespaces][stateNodeName].items;
			const item = find(items, entity => entity.name === controlName);
			const shouldCloseControl = item.options.length === item.options.filter(option => option.isSelected).length;

			if (item.isSubPermission) {
				const shouldCloseRelationRule = item.options.filter(option => option.isSelected).length;
				const parentPermissionItem = find(
					items, entity => entity.subPermission && entity.subPermission.moduleKey === item.name
				);

				shouldCloseRelationRule === 0 && (
					dispatch(actions.permissionControlToggleSelectValue(
						parentPermissionItem.name,
						parentPermissionItem.subPermission.relationRule
					))
				);
			}

			if (shouldCloseControl) {
				dispatch(actions.permissionsControlToggle(controlName));
			}

		},

		permissionsControlSetInitialValues: (
			permissionSelectOptions,
			permissionValues,
			subPermissionValues,
		) => (dispatch, getState) => {
			const state = getState();
			const currentAdmin = state.root.data;
			const editableAdminId = window.location.pathname.split('/').pop();
			const editableAdmin = find(state.admins.data, { id: parseInt(editableAdminId) });
			dispatch({
				type: SET_INITIAL_VALUES,
				payload: {
					permissionSelectOptions,
					permissionValues,
					subPermissionValues,
					currentAdmin,
					editableAdmin
				}
			});
		},

		permissionsControlSave: adminId => (dispatch, getState) => {
			const { admins: { data, permissions: { items } } } = getState();
			const adminInfo = find(data, { id: parseInt(adminId) });
			const hiddenPermissions = parentSelectors.getHiddenPermissions(getState());
			const handleSubpermission = shouldHandleSubpermission(getState().root.data, adminInfo);
			const permissions = getSelectedPermissions(items, handleSubpermission);

			if (!isEmpty(hiddenPermissions) && !isEmpty(adminInfo.permissions)) {
				hiddenPermissions.forEach(hidden => {
					if (
						adminInfo.permissions.indexOf(hidden.value) !== -1 &&
						permissions.indexOf(hidden.value) === -1
					) {
						permissions.push(hidden.value);
					}
				});
			}

			delete adminInfo.substituting_admins;
			delete adminInfo.sub_ids;

			const request = http({
				url: `${endpoints.ADMINS_LIST}/${adminId}`,
				method: 'put',
				actions: [
					SAVE_PERMISSION_SUCCESS,
					SAVE_PERMISSION_ERROR
				],
				data: {
					...adminInfo,
					permissions
				}
			});

			dispatch(parentActions.toggleProgressBar());

			return request(dispatch).then(action => {
				if (action.type === SAVE_PERMISSION_SUCCESS) {
					dispatch(parentActions.redirectToModuleIndex());
				}
				if (action.type === SAVE_PERMISSION_ERROR) {
					const message = get(action, 'payload.response.data[0].message');
					message && alert(message);
				}
			});
		},

		permissionControlCancel: () => dispatch => {
			dispatch(parentActions.redirectToModuleIndex());
		}
	};

	// reducers
	const reducers = {
		[TOGGLE_SELECT]: (state, action) => {
			const controlName = action.payload;
			const items = state[stateNodeName].items.map(entity => ({
				...entity,
				isOpen: entity.name === controlName ? !entity.isOpen : false
			}));
			return {
				...state,
				[stateNodeName]: {
					items
				}
			};
		},

		[TOGGLE_SELECT_VALUE]: (state, action) => {
			const { controlName, optionValue } = action.payload;
			const items = state[stateNodeName].items.map(entity => ({
				...entity,
				options: entity.options.map(option => ({
					...option,
					isSelected: entity.name === controlName && option.value === optionValue
						? !option.isSelected : option.isSelected
				}))
			}));
			return {
				...state,
				[stateNodeName]: {
					items
				}
			};
		},

		[TOGGLE_SUBPERMISSION]: (state, action) => {
			const { controlName } = action.payload;
			const item = find(state[stateNodeName].items, entity => entity.name === controlName);

			if (item.subPermission) {
				const items = state[stateNodeName].items;
				const relationRule = find(item.options, entity => entity.value === item.subPermission.relationRule);
				const subPermission = find(items, entity => entity.name === item.subPermission.moduleKey);
				const subPermissionIndex = findIndex(items, entity => entity.name === item.subPermission.moduleKey);

				const cloneItems = cloneDeep(items);

				cloneItems[subPermissionIndex] = {
					...subPermission,
					isVisible: !!relationRule.isSelected
				};

				return {
					...state,
					[stateNodeName]: {
						items: cloneItems
					}
				};
			}

			return state;
		},

		[SET_INITIAL_VALUES]: (state, action) => {
			const {
				permissionSelectOptions, permissionValues,
				subPermissionValues, currentAdmin, editableAdmin
			} = action.payload;

			const permissions = currentAdmin.id === editableAdmin.id ? permissionValues : currentAdmin.permissions;

			const hasGlobalPermission = permissions.indexOf('admins_set_global_permissions') !== -1;

			const cloneItems = cloneDeep(state[stateNodeName].items);

			const items = cloneItems.map(entity => {
				const filterOptions = entity.options
					.filter(option => (hasGlobalPermission || find(permissions, permission => permission === option.value)))
					.filter(option => !(option.getIsHidden && option.getIsHidden()));

				const options = filterOptions.map(option => ({
					...option,
					isSelected: find(permissionSelectOptions, permission => permission.value === option.value)
				}));

				return {
					...entity,
					options
				};
			});

			// create payout subpermission item
			const handleSubpermission = shouldHandleSubpermission(currentAdmin, editableAdmin);

			if (handleSubpermission) {
				const relationParent = find(items, entity => entity.subPermission);
				const relationParentRule = find(
					relationParent.options, entity => entity.value === relationParent.subPermission.relationRule
				);

				if (relationParent && relationParentRule) {
					const relationParentIndex = findIndex(items, entity => entity.subPermission);
					const relationSubPermissionsValue = find(subPermissionValues, entity => {
						return entity.relationRule === relationParent.subPermission.relationRule;
					});

					const payoutSubPermission = {
						isOpen: false,
						isVisible: !!relationParentRule.isSelected,
						name: relationParent.subPermission.moduleKey,
						placeholder: relationParent.subPermission.moduleName,
						isSubPermission: true,
						options: state.payouts.map(entity => {
							const subPermissionsValue = relationSubPermissionsValue && relationSubPermissionsValue.value.split(',');
							const isSelected = subPermissionsValue && !!subPermissionsValue.filter(
								permission => permission == entity.id
							).length;

							return {
								label: entity.title,
								value: entity.id,
								icon: 'icon-view',
								isSelected
							};
						})
					};

					items.splice(relationParentIndex + 1, 0, payoutSubPermission);
				}
			}

			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					items
				}
			};
		},

		[SAVE_PERMISSION_ERROR]: (state, action) => {
			const message = get(action, 'payload.response.data[0].message');
			//Do not show default SWW message if there is meaningful message
			return { ...state, error: !message, showProgressBar: false };
		}
	};

	// selectors
	const selectors = {
		getPermissionsList: state => state[namespaces][stateNodeName].items &&
			state[namespaces][stateNodeName].items.filter(entity => !!entity.options.length && entity.isVisible)
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

export default createPermissions;

// functions
const getSelectedPermissions = (items, withSubpermissions = true) => {
	const withoutSubPermission = items.filter(entity => !entity.isSubPermission);

	const result = withoutSubPermission.reduce((acc, entity) => {
		const hasSubPermission = !!entity.subPermission;
		const selectedOptions = entity.options.filter(rule => rule.isSelected).map(rule => {
			if (hasSubPermission && withSubpermissions) {
				const { moduleKey, relationRule } = entity.subPermission;
				const subPermissionItem = find(items, rule => rule.isSubPermission && rule.name === moduleKey);
				const subPermissionOptions = subPermissionItem.options.filter(rule => rule.isSelected).map(rule => rule.value);

				if (relationRule === rule.value) {
					return subPermissionOptions.length ? `${rule.value}:[${subPermissionOptions.join(',')}]` : false;
				}
			}

			return rule.value;
		});

		return [].concat(acc, compact(selectedOptions));
	}, []);

	return result;
};

const shouldHandleSubpermission = (currentAdmin, editableAdmin) => {
	const isSame = currentAdmin.id === editableAdmin.id;
	const currentAdminHasSetGlobalPermission = currentAdmin.permissions.indexOf('admins_set_global_permissions') > -1;
	const editableAdminHasGlobalPermission = editableAdmin.permissions.indexOf('admins_set_global_permissions') > -1;

	return isSame ? !currentAdminHasSetGlobalPermission : !editableAdminHasGlobalPermission;
};


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/admins/components/permissions/index.js